import fastdom from 'fastdom';

import { DOMHelper } from 'core/DOMHelper';
import ProjectHeaderSizes from 'app/views/project/ProjectHeaderSizes';
import { Simple } from 'utils/analytics';
import { ProjectScrollDeps } from './project.types';
import { device } from 'utils/device';

/**
 * Per-frame scroll state for the project section: the circular close
 * progress, the UI theme switch when scrolling past the header, the WebGL
 * pause join, page-bottom / close-button visibility (+ analytics) and the
 * back-nav scroll position record. Scrollbar / Pixi / work-tile interop is
 * resolved in ProjectController and arrives via the injected deps.
 */
export class ProjectScroll {
	private _dom: DOMHelper;
	private _deps: ProjectScrollDeps;

	public scrollProgress: number = 0;
	public headerHeight: number = 0;
	public webGLActive: boolean = true;
	public lowerUIThemeActive: boolean = false;
	public closeButtonVisible: boolean = true;
	public bottomScrollReached: boolean = false;
	public allowScrollTweenUpdate: boolean = false;

	public projectPageBottomTracked: boolean = false;
	public projectPageMiddleTracked: boolean = false;


	constructor(dom: DOMHelper, deps: ProjectScrollDeps) {
		this._dom = dom;
		this._deps = deps;
	}

	/** Per-frame scroll update (delegated from ProjectController.scroll) */
	public scroll(y: number): void {
		if (this._deps.isPaused() || !this._deps.isActive()) {
			if (this.allowScrollTweenUpdate) {
				this.scrollProgress = this._deps.getRealProgress();

				fastdom.mutate(() => {
					this._deps
						.getCircleProgressEl()
						.style.setProperty('--page-progess-deg', `${Math.round(this.scrollProgress * 100 * 3.6)}deg`);
				});
			}

			return;
		}

		try {
			this.scrollProgress = this._deps.getRealProgress();

			fastdom.mutate(() => {
				this._deps
					.getCircleProgressEl()
					.style.setProperty('--page-progess-deg', `${this.scrollProgress * 100 * 3.6}deg`);
			});

			if (!this._deps.isDeeplink()) {
				//Parallax on Work Tile / floating elemnts
				if (this.webGLActive) {
					this._deps.scrollActiveTile(y);
				}

				if (this._deps.isPixiScrollActive()) {
					if (this.webGLActive && y > this.headerHeight) {
						this.webGLActive = false;
						this._deps.onWebGLPause();
					} else if (!this.webGLActive && y <= this.headerHeight) {
						this.webGLActive = true;
						this._deps.onWebGLUnpause();
					}
				}
			}

			if (this._deps.isClosing()) {
				return;
			}
			let ratio = Math.min(y / device.innerHeight, 1);

			if (ratio < 1 && y) {
				Math.max(y / device.innerHeight, 0);
			}

			this.headerHeight = ProjectHeaderSizes.headerHeight();

			if (!this.lowerUIThemeActive && y > this.headerHeight - 60) {
				this._deps.setUiThemeType('dark');
				this.lowerUIThemeActive = true;
				this._setScrollPastHeader(true);
			} else if (this.lowerUIThemeActive && y <= this.headerHeight - 60) {
				this._deps.setUiThemeType(this._deps.getTopUITheme());
				this.lowerUIThemeActive = false;
				this._setScrollPastHeader(false);
			}

			let maxScroll = Math.abs(this._deps.getMaxScroll());
			let showPageBottom = this.scrollProgress * maxScroll > maxScroll - (this._deps.getInnerHeight() - 400);

			if (!this.projectPageMiddleTracked && this.scrollProgress > 0.5) {
				this.projectPageMiddleTracked = true;
				Simple.event('project_page_middle_reached', { slug: this._deps.getActiveSlug() });
			}

			if (!this.bottomScrollReached && showPageBottom) {
				this.bottomScrollReached = true;
				this._dom.addClass(this._deps.getContainer(), '-pagebottom');

				if (!this.projectPageBottomTracked) {
					Simple.event('project_page_bottom_reached', { slug: this._deps.getActiveSlug() });
					this.projectPageBottomTracked = true;
				}

				this._dom.addClass(document.querySelector('body'), '-projectpagebottom');
			} else if (this.bottomScrollReached && !showPageBottom) {
				this.bottomScrollReached = false;
				this._dom.removeClass(this._deps.getContainer(), '-pagebottom');
				this._dom.removeClass(document.querySelector('body'), '-projectpagebottom');
			}

			if (!this.closeButtonVisible && this.scrollProgress <= 0.001) {
				this._dom.addClass(this._deps.getContainer(), '-closevisible');
				this.closeButtonVisible = true;
			} else if (this.closeButtonVisible && this.scrollProgress > 0.001) {
				this._dom.removeClass(this._deps.getContainer(), '-closevisible');
				this.closeButtonVisible = false;
			}
		} catch (e) {}
	}

	/** Record the content Y so the position survives the nav-menu detour */
	public matchContentYPosition(): void {
		if (this._deps.isNativeScroller()) {
			this._deps.getPlaneY();
		}
	}

	private _setScrollPastHeader(past): void {
		if (past) {
			this._dom.addClass(this._deps.getContainer(), '-scrollpastheader');
		} else {
			this._dom.removeClass(this._deps.getContainer(), '-scrollpastheader');
		}
	}
}
